import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { apiClient, CloudResource, VPCResourceGroup } from '../api/client';
import { ASSIGNABLE_RESOURCE_TYPES, ALL_CATEGORY_NAMES } from '../constants/cloudResources';

interface UseCloudResourceFilterOptions {
  /** Number of items per page. Default: 20 */
  pageSize?: number;
  /**
   * If true, VPCs are fetched via getCloudResourceVPCs.
   * Used by TeamManagement and PagerDuty.
   */
  enableVPCs?: boolean;
  /**
   * When false, the hook's fetch effects will not fire.
   * Use this to gate fetching on dialog open state.
   * Default: true.
   */
  enabled?: boolean;
}

interface UseCloudResourceFilterReturn {
  resources: CloudResource[];
  vpcs: VPCResourceGroup[];
  loading: boolean;
  page: number;
  totalPages: number;
  totalCount: number;
  searchTerm: string;
  selectedCategories: Set<string>;
  selectedResourceTypes: string | undefined;
  showVpcGroups: boolean;
  setSearchTerm: (term: string) => void;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  handleCategoryChange: (category: string, checked: boolean) => void;
  toggleAllCategories: () => void;
  refetch: () => void;
  refetchVPCs: () => void;
  reset: () => void;
}

export function useCloudResourceFilter(options: UseCloudResourceFilterOptions = {}): UseCloudResourceFilterReturn {
  const {
    pageSize = 20,
    enableVPCs = false,
    enabled = true,
  } = options;

  const [resources, setResources] = useState<CloudResource[]>([]);
  const [vpcs, setVpcs] = useState<VPCResourceGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(
    new Set(ALL_CATEGORY_NAMES)
  );

  // Track whether this is the initial mount to avoid double-fetch
  const initialFetchDone = useRef(false);

  // VPC/VCN types that are shown as VPC groups instead of individual resources
  const VPC_TYPES = useMemo(() => new Set(['AWS::EC2::VPC', 'OCI::Core::VCN']), []);

  const showVpcGroups = selectedCategories.has('Network');

  // Compute resource_types from selected categories using assignable types
  // (Network only includes VPCs/VCNs — child resources are owned via parent VPC)
  // When VPC groups are enabled, exclude VPC/VCN from individual resources to avoid duplicates
  const selectedResourceTypes = useMemo(() => {
    if (selectedCategories.size === 0) return '__none__';
    const types: string[] = [];
    selectedCategories.forEach(cat => types.push(...(ASSIGNABLE_RESOURCE_TYPES[cat] || [])));
    const filtered = enableVPCs && showVpcGroups
      ? types.filter(t => !VPC_TYPES.has(t))
      : types;
    return filtered.length > 0 ? filtered.join(',') : '__none__';
  }, [selectedCategories, enableVPCs, showVpcGroups, VPC_TYPES]);

  // Debounce search (500ms)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch resources
  const fetchResources = useCallback(async (p: number, search: string) => {
    setLoading(true);
    try {
      const response = await apiClient.getCloudResources({
        page: p,
        page_size: pageSize,
        search: search || undefined,
        resource_types: selectedResourceTypes,
      });
      setResources(response?.resources || []);
      setTotalCount(response?.total_count || 0);
      setTotalPages(response?.total_pages || 1);
    } catch (err) {
      console.error('Failed to fetch cloud resources:', err);
    } finally {
      setLoading(false);
    }
  }, [pageSize, selectedResourceTypes]);

  // Fetch VPCs
  const fetchVPCs = useCallback(async () => {
    if (!enableVPCs) return;
    try {
      const response = await apiClient.getCloudResourceVPCs();
      setVpcs(response?.vpcs || []);
    } catch (err) {
      console.error('Failed to fetch VPCs:', err);
    }
  }, [enableVPCs]);

  // Auto-fetch when search/category filter changes — reset to page 1
  useEffect(() => {
    if (!enabled) return;
    setPage(1);
    fetchResources(1, debouncedSearch);
    initialFetchDone.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, selectedResourceTypes, enabled]);

  // Fetch on page change (user pagination)
  useEffect(() => {
    if (!enabled || !initialFetchDone.current) return;
    // The effect above handles page=1 resets; this handles explicit page changes
    fetchResources(page, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Handlers
  const handleCategoryChange = useCallback((category: string, checked: boolean) => {
    setSelectedCategories(prev => {
      const next = new Set(prev);
      if (checked) next.add(category);
      else next.delete(category);
      return next;
    });
    setPage(1);
  }, []);

  const toggleAllCategories = useCallback(() => {
    setSelectedCategories(prev =>
      prev.size === ALL_CATEGORY_NAMES.length ? new Set<string>() : new Set(ALL_CATEGORY_NAMES)
    );
    setPage(1);
  }, []);

  // Filter VPCs client-side by search term (VPCs are fetched once without search)
  const filteredVpcs = useMemo(() => {
    if (!debouncedSearch) return vpcs;
    const search = debouncedSearch.toLowerCase();
    return vpcs.filter(vpc =>
      vpc.vpc_name?.toLowerCase().includes(search) ||
      vpc.vpc_id.toLowerCase().includes(search)
    );
  }, [vpcs, debouncedSearch]);

  const refetch = useCallback(() => {
    fetchResources(page, debouncedSearch);
  }, [fetchResources, page, debouncedSearch]);

  const reset = useCallback(() => {
    setSearchTerm('');
    setDebouncedSearch('');
    setPage(1);
    setSelectedCategories(new Set(ALL_CATEGORY_NAMES));
    initialFetchDone.current = false;
  }, []);

  return {
    resources, vpcs: filteredVpcs, loading, page, totalPages, totalCount,
    searchTerm, selectedCategories,
    selectedResourceTypes, showVpcGroups,
    setSearchTerm, setPage, handleCategoryChange, toggleAllCategories,
    refetch, refetchVPCs: fetchVPCs, reset,
  };
}
